import {Meta} from '@rdcomponents/meta/meta';
import {Table, Typography} from 'antd';
import {useContext, useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {appRoute} from '../../../../../app-route';
import {BreadCrumbs} from '../../../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../../../layout/page/page';
import {MainPageContainer} from '../../../../../layout/page-card/main-page-container';
import {PageCard} from '../../../../../layout/page-card/page-card';
import {PageHeader} from '../../../../../layout/page-header/page-header';
import {FormattedNumber} from '../../../../../provider/locale/formatted-number';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../provider/locale/localization';
import {MainFilterContext} from '../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../provider/main-filter/main-filter-type';
import {useCompany} from '../../../../../service/company/company-hook';
import {useReviewAnalyticsTagBySentiment} from '../../../../../service/reivew-analysis/review-analytics-tags/review-analytics-tag-hook';
import {
    useReviewAnalyticsAspectsByCompany,
    useReviewsCountByAspectsByCompany,
} from '../../../../../service/reivew-analysis/review-aspects/review-aspects-hook';
import {
    ReviewAspectRecordType,
    ReviewAspectTableSortedColumnsEnum,
} from '../../../../../service/reivew-analysis/review-aspects/review-aspects-type';
import {
    useReviewCompetitorBrands,
    useReviewCustomerBrands,
} from '../../../../../service/reivew-analysis/review-competitors/review-competitors-hook';
import {AiSummaryBlock} from '../../components/ai-summary-block/ai-summary-block';
import {AspectNameList} from '../../components/aspect-name-list/aspect-name-list';
import {AspectScatter, ScatterPointType} from '../../components/aspect-scatter/aspect-scatter';
import {SentimentBar} from '../../components/sentiment-bar/sentiment-bar';
import {useReviewsAnalysisDefaultDateRange, useReviewsAnalysisFilter} from '../../reviews-analysis-hook';
import {useReviewBrands} from '../dashboard/dashboard-hook';
import {TagCloudChart} from '../dashboard/widgets/tag-cloud/tag-cloud-chart/tag-cloud-chart';
import {tagCloudFetchCount, tagCloudSingleLimit} from '../dashboard/widgets/tag-cloud/tag-cloud-const';
import {getTonalityBandColor, getTonalitySentimentData} from '../keywords-and-topics-page/aspects-tab/aspects-helper';
import {AspectStrengthEnum} from '../keywords-and-topics-page/aspects-tab/aspects-tab-type';
import {keywordsAndTopicsPageSizeOptions} from '../keywords-and-topics-page/keywords-and-topics-page-const';

import {getStrongWeakAspectsFromReviews} from './aspect-location-const';
import * as styles from './aspect-location-page.scss';

export function AspectLocationPage(): JSX.Element {
    const companyId = Number(useParams<{companyId: string}>().companyId);

    const {getLocalizedString} = useLocale();
    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);
    // Opened outside the filter drawer — seed a default period so date-scoped endpoints
    // (aspects, tags, AI summary) don't query all-time or 400 on empty dates.
    const defaultDateRange = useReviewsAnalysisDefaultDateRange();
    const {singleFilter, competitorFilter} = useReviewsAnalysisFilter(true, defaultDateRange);

    const {reviewsAnalysisDashboard, reviewsAnalysisKeywordsAndTopics, reviewsAnalysisAspectLocation} = appRoute;

    const {result: company} = useCompany(companyId);

    const reviewsByAspects = useReviewsCountByAspectsByCompany(companyId, defaultDateRange);
    const aspects = useReviewAnalyticsAspectsByCompany(companyId, defaultDateRange);
    const {getSortOrder, setSorterOrder} = aspects.sorter;

    // Memoize so the location-scoped array isn't a fresh reference each render — the tags
    // hook keys its pagination on these fields and would otherwise refetch in a loop.
    const tagCloudOptions = useMemo(() => ({...singleFilter, companyIds: [companyId]}), [singleFilter, companyId]);

    const tagCloud = useReviewAnalyticsTagBySentiment(tagCloudOptions, mainFilterKey, {
        initialPageSize: tagCloudFetchCount,
        shouldSaveState: false,
    });

    // Competitor comparison is brand-level (your brand vs competitor brands), so it is
    // NOT scoped to this location — competitors have their own company_ids.
    const competitors = useReviewCompetitorBrands(competitorFilter, mainFilterKey);
    const customer = useReviewCustomerBrands(singleFilter, mainFilterKey);
    const {
        result: {nameMap},
    } = useReviewBrands();

    const reviewsByAspectsList = reviewsByAspects.data?.reviewsByAspects ?? [];
    const strongAspects = getStrongWeakAspectsFromReviews(reviewsByAspectsList, AspectStrengthEnum.Strong);
    const weakAspects = getStrongWeakAspectsFromReviews(reviewsByAspectsList, AspectStrengthEnum.Weak);

    const scatterPoints: Array<ScatterPointType> = useMemo(() => {
        const points: Array<ScatterPointType> = (competitors.result ?? []).map((brand) => ({
            name: brand.brandId === null ? '' : nameMap[brand.brandId] ?? '',
            tonalityIdx: brand.tonalityIndexSimple,
            mentions: brand.total,
            isOwn: false,
        }));

        if (customer.result) {
            points.push({
                name: '',
                tonalityIdx: customer.result.tonalityIndexSimple,
                mentions: customer.result.total,
                isOwn: true,
            });
        }

        return points;
    }, [competitors.result, customer.result, nameMap]);

    return (
        <Page renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <Meta title={getLocalizedString('REVIEWS_ANALYSIS__ASPECT_LOCATION__TITLE')} />

            <PageCard>
                <BreadCrumbs
                    list={[
                        {path: reviewsAnalysisDashboard.path, titleLangKey: 'CATEGORY_NAME__REVIEWS_ANALYSIS'},
                        {
                            path: reviewsAnalysisKeywordsAndTopics.path,
                            titleLangKey: 'CATEGORY_NAME__REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS',
                        },
                        {
                            path: reviewsAnalysisAspectLocation.path,
                            titleLangKey: 'REVIEWS_ANALYSIS__ASPECT_LOCATION__TITLE',
                        },
                    ]}
                />

                <PageHeader>
                    <div>
                        <Typography.Title level={4}>
                            {getLocalizedString('REVIEWS_ANALYSIS__ASPECT_LOCATION__TITLE')} «{company?.name ?? ''}»
                        </Typography.Title>
                        {company?.address?.string_address ? (
                            <Typography.Text type="secondary">{company.address.string_address}</Typography.Text>
                        ) : null}
                    </div>
                </PageHeader>
            </PageCard>

            <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECT_LOCATION__STRONG_WEAK_TITLE" />}>
                <div className={styles.AspectLocation_strongWeak}>
                    <div className={styles.AspectLocation_column}>
                        <div className={styles.AspectLocation_columnTitle}>
                            <Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_BY_LOCATIONS__STRONG_SIDES" />
                        </div>
                        <AspectNameList names={strongAspects} />
                    </div>
                    <div className={styles.AspectLocation_column}>
                        <div className={styles.AspectLocation_columnTitle}>
                            <Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_BY_LOCATIONS__WEAK_SIDES" />
                        </div>
                        <AspectNameList names={weakAspects} />
                    </div>
                </div>
            </PageCard>

            <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECT_LOCATION__ASPECTS_LIST_TITLE" />}>
                <Table<ReviewAspectRecordType>
                    dataSource={aspects.data?.results || []}
                    loading={aspects.isLoading}
                    onChange={(_pagination, _filters, sorter) => {
                        setSorterOrder(sorter);
                    }}
                    pagination={{
                        total: aspects.pagination.total,
                        pageSize: aspects.pagination.pageSize,
                        current: aspects.pagination.page,
                        pageSizeOptions: keywordsAndTopicsPageSizeOptions,
                        showSizeChanger: true,
                        onChange: aspects.pagination.onChange,
                    }}
                    rowKey={(record) => record.ids.join('-')}
                    scroll={{x: true}}
                    size="middle"
                >
                    <Table.Column<ReviewAspectRecordType>
                        render={(_value, record) => record.name}
                        title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NAME" />}
                    />
                    <Table.Column<ReviewAspectRecordType>
                        align="center"
                        dataIndex={ReviewAspectTableSortedColumnsEnum.mainNegativeReviews}
                        render={(_value, record) => record.negativeReviews.value}
                        sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNegativeReviews)}
                        sorter
                        title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEGATIVE" />}
                    />
                    <Table.Column<ReviewAspectRecordType>
                        align="center"
                        dataIndex={ReviewAspectTableSortedColumnsEnum.mainNeutralReviews}
                        render={(_value, record) => record.neutralReviews.value}
                        sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNeutralReviews)}
                        sorter
                        title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEUTRAL" />}
                    />
                    <Table.Column<ReviewAspectRecordType>
                        align="center"
                        dataIndex={ReviewAspectTableSortedColumnsEnum.mainPositiveReviews}
                        render={(_value, record) => record.positiveReviews.value}
                        sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainPositiveReviews)}
                        sorter
                        title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__POSITIVE" />}
                    />
                    <Table.Column<ReviewAspectRecordType>
                        align="center"
                        dataIndex={ReviewAspectTableSortedColumnsEnum.mainTonalityIndex}
                        render={(_value, record) => (
                            <span
                                className={styles.AspectLocation_indexValue}
                                style={{color: getTonalityBandColor(record.tonalityIdx.value)}}
                            >
                                <FormattedNumber
                                    options={{maximumFractionDigits: 1}}
                                    value={record.tonalityIdx.value}
                                />
                            </span>
                        )}
                        sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainTonalityIndex)}
                        sorter
                        title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__SENTIMENT_INDEX" />}
                    />
                    <Table.Column<ReviewAspectRecordType>
                        align="center"
                        dataIndex={ReviewAspectTableSortedColumnsEnum.mainTotalReviews}
                        render={(_value, record) => record.totalReviews.value}
                        sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainTotalReviews)}
                        sorter
                        title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__MENTIONS_COUNT" />}
                    />
                    <Table.Column<ReviewAspectRecordType>
                        align="center"
                        render={(_value, record) => (
                            <SentimentBar
                                banUnspecified
                                sentimentData={getTonalitySentimentData(record.tonalityCurrentPeriodData)}
                                showPercentages
                            />
                        )}
                        title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__DISTRIBUTION" />}
                    />
                </Table>
            </PageCard>

            <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECT_LOCATION__KEYWORDS_TITLE" />}>
                <TagCloudChart
                    data={tagCloud.result?.results || []}
                    isInProgress={tagCloud.isInProgress}
                    processError={tagCloud.processError}
                    tagLimit={tagCloudSingleLimit}
                />
            </PageCard>

            <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECT_LOCATION__AI_SUMMARY_TITLE" />}>
                <AiSummaryBlock companyId={companyId} />
            </PageCard>

            <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECT_LOCATION__COMPETITORS_TITLE" />}>
                <AspectScatter points={scatterPoints} />
            </PageCard>
        </Page>
    );
}
